import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Input, useDebounceCallback } from '@devhop/ui';
import { SearchIcon } from 'lucide-react';
import { SimpleSelect } from '@/shared/ui/SimpleSelect';

type Option = { value: string; label: string };

export type PromotionRulesSearchParams = {
    offset?: number;
    limit?: number;
    sort?: Array<{ field: string; dir: 'asc' | 'desc' }>;
    isActive?: boolean;
    fromPositionId?: string;
    toPositionId?: string;
    search?: string;
};

type Props = {
    searchParams: PromotionRulesSearchParams;
    fromRoute: '/app/hr/promotion/rules';
    positionOptions: Option[];
};

const VALUE_ALL = '__all__';

export function PromotionRulesFilter({
    searchParams,
    fromRoute,
    positionOptions,
}: Props) {
    const nav = useNavigate({ from: fromRoute });
    const [searchInput, setSearchInput] = useState(searchParams.search ?? '');
    const [fromPositionId, setFromPositionId] = useState(
        searchParams.fromPositionId ?? VALUE_ALL,
    );
    const [toPositionId, setToPositionId] = useState(
        searchParams.toPositionId ?? VALUE_ALL,
    );
    const [statusInput, setStatusInput] = useState(
        searchParams.isActive === true
            ? 'true'
            : searchParams.isActive === false
              ? 'false'
              : VALUE_ALL,
    );

    useEffect(() => {
        setSearchInput(searchParams.search ?? '');
        setFromPositionId(searchParams.fromPositionId ?? VALUE_ALL);
        setToPositionId(searchParams.toPositionId ?? VALUE_ALL);
        setStatusInput(
            searchParams.isActive === true
                ? 'true'
                : searchParams.isActive === false
                  ? 'false'
                  : VALUE_ALL,
        );
    }, [
        searchParams.search,
        searchParams.fromPositionId,
        searchParams.toPositionId,
        searchParams.isActive,
    ]);

    const doNav = (opts: Partial<{ fromId: string; toId: string; status: string; searchVal: string }> = {}) => {
        const f = opts.fromId ?? fromPositionId;
        const t = opts.toId ?? toPositionId;
        const st = opts.status ?? statusInput;
        const s = opts.searchVal ?? searchInput;
        nav({
            search: {
                offset: 0,
                limit: searchParams.limit ?? 20,
                sort: searchParams.sort,
                isActive: st === VALUE_ALL ? undefined : st === 'true',
                fromPositionId: f === VALUE_ALL ? undefined : f,
                toPositionId: t === VALUE_ALL ? undefined : t,
                search: s.trim() || undefined,
            },
        });
    };

    const debouncedSearch = useDebounceCallback((val: string) => doNav({ searchVal: val }), 400);

    const positionSelectOptions = [
        { value: VALUE_ALL, label: 'ທັງໝົດ' },
        ...positionOptions,
    ];

    return (
        <div className="mb-4 flex flex-col gap-2 px-2">
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative min-w-[200px] flex-1">
                    <SearchIcon className="absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        value={searchInput}
                        onChange={(e) => {
                            setSearchInput(e.target.value);
                            debouncedSearch(e.target.value);
                        }}
                        placeholder="ຄົ້ນຫາຕຳແໜ່ງ..."
                        className="h-8 pl-8 sm:max-w-2xs"
                    />
                </div>
                <SimpleSelect
                    options={positionSelectOptions}
                    value={fromPositionId}
                    onValueChange={(v) => {
                        setFromPositionId(v);
                        doNav({ fromId: v });
                    }}
                    placeholder="ຈາກຕຳແໜ່ງ"
                    className="h-8 w-full sm:w-40"
                />
                <SimpleSelect
                    options={positionSelectOptions}
                    value={toPositionId}
                    onValueChange={(v) => {
                        setToPositionId(v);
                        doNav({ toId: v });
                    }}
                    placeholder="ໄປຕຳແໜ່ງ"
                    className="h-8 w-full sm:w-40"
                />
                <SimpleSelect
                    options={[
                        { value: VALUE_ALL, label: 'ທັງໝົດ' },
                        { value: 'true', label: 'ACTIVE' },
                        { value: 'false', label: 'INACTIVE' },
                    ]}
                    value={statusInput}
                    onValueChange={(v) => {
                        setStatusInput(v);
                        doNav({ status: v });
                    }}
                    placeholder="ສະຖານະ"
                    className="h-8 w-full sm:w-32"
                />
            </div>
        </div>
    );
}
